import type { SortKey } from '@/constants/companies'

import { useMemo, useState } from 'react'
import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import CountBadge from '@/components/ui/count-badge'
import Field from '@/components/ui/field'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ownerByName } from '@/constants/companies'
import { ALL_OWNERS, activeFilterCount, filterCompanies } from '@/lib/companies'
import { cn } from '@/lib/utils'
import { useCrmStore } from '@/stores/crm-store-provider'
import FilterIcon from '@/assets/images/_common/filter.svg'
import XIcon from '@/assets/images/companies/detail/x.svg'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

import {
    ACTIVITY_OPTIONS,
    OWNER_OPTIONS,
    SORT_MENU_OPTIONS,
    STAGE_OPTIONS
} from '@/components/companies/toolbar/filter-options'

interface MobileFiltersProps {
    className?: string
}

export default function MobileFilters({ className }: MobileFiltersProps) {
    const [open, setOpen] = useState(false)
    const companies = useCrmStore(state => state.companies)
    const sortBy = useCrmStore(state => state.sortBy)
    const owner = useCrmStore(state => state.owner)
    const stage = useCrmStore(state => state.stage)
    const activityWindow = useCrmStore(state => state.activityWindow)
    const setSortBy = useCrmStore(state => state.setSortBy)
    const setOwner = useCrmStore(state => state.setOwner)
    const setStage = useCrmStore(state => state.setStage)
    const setActivityWindow = useCrmStore(state => state.setActivityWindow)
    const resetFilters = useCrmStore(state => state.resetFilters)

    const activeCount = activeFilterCount({ sortBy, owner, stage, activityWindow })
    const resultCount = useMemo(
        () => filterCompanies(companies, { sortBy, owner, stage, activityWindow }).length,
        [companies, sortBy, owner, stage, activityWindow]
    )

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <Button
                variant='secondary'
                size='sm'
                onClick={() => setOpen(true)}
                aria-label={activeCount > 0 ? `Filters, ${activeCount} active` : "Filters"}
                className={cn('data-[active=true]:bg-muted', className)}
                data-active={activeCount > 0}
            >
                <FilterIcon aria-hidden className='size-3' />
                Filters
                {activeCount > 0 && <CountBadge>{activeCount}</CountBadge>}
            </Button>

            <SheetContent side='bottom'>
                <SheetHeader className='px-4'>
                    <SheetTitle>Filters</SheetTitle>
                    <SheetDescription className='sr-only'>
                        Sort and filter the companies table
                    </SheetDescription>
                    <SheetClose asChild>
                        <Button
                            variant='ghost'
                            size='icon-sm'
                            className='-mr-1'
                            aria-label="Close filters"
                        >
                            <XIcon aria-hidden className='text-foreground size-4' />
                        </Button>
                    </SheetClose>
                </SheetHeader>

                <ScrollArea viewportClassName='max-h-[calc(85dvh-118px)]'>
                    <div className='flex flex-col gap-4 p-4'>
                        <Field label="Sort by" htmlFor='mobile-sort'>
                            <Select value={sortBy} onValueChange={value => setSortBy(value as SortKey)}>
                                <SelectTrigger id='mobile-sort'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SORT_MENU_OPTIONS.map(option => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field label="Account owner" htmlFor='mobile-owner'>
                            <Select value={owner} onValueChange={setOwner}>
                                <SelectTrigger id='mobile-owner'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {OWNER_OPTIONS.map(option => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.value === ALL_OWNERS ? option.label : (
                                                <span className='flex items-center gap-2'>
                                                    <Avatar src={ownerByName(option.value).avatar} alt='' />
                                                    {option.label}
                                                </span>
                                            )}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field label="Stage" htmlFor='mobile-stage'>
                            <Select value={stage} onValueChange={setStage}>
                                <SelectTrigger id='mobile-stage'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STAGE_OPTIONS.map(option => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field label="Last activity" htmlFor='mobile-activity'>
                            <Select
                                value={String(activityWindow)}
                                onValueChange={value => setActivityWindow(Number(value))}
                            >
                                <SelectTrigger id='mobile-activity'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ACTIVITY_OPTIONS.map(option => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                </ScrollArea>

                <SheetFooter className='px-4'>
                    <Button
                        variant='ghost'
                        size='sm'
                        onClick={resetFilters}
                        disabled={activeCount === 0 && sortBy === 'pipelineValue'}
                        className='-ml-1.5'
                    >
                        Reset
                    </Button>
                    <SheetClose asChild>
                        <Button variant='primary' size='sm'>
                            Show {resultCount} {resultCount === 1 ? "company" : "companies"}
                        </Button>
                    </SheetClose>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    )
}
